import { Close } from "@mui/icons-material";
import { Box, Dialog, IconButton, TextField, Typography } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useCallback, useContext, useEffect, useState } from "react";
import constants from "../../config/constants";
import { AlertContext } from "../../context/alert.context";
import { useLanguage } from "../../context/language.context";

// GH-707: an expert can define several vacation ranges. The list comes merged from the server
// (a vacation set with the old single-vacation dialog shows here too, id 'legacy').
export type VacationRow = {
  _id: string;
  start: string;
  end: string;
  legacy?: boolean;
};

const VacationsList = () => {
  const { t, dir, lang } = useLanguage();
  const alert = useContext(AlertContext);
  const [rows, setRows] = useState<VacationRow[]>([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ start?: string; end?: string }>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get<VacationRow[]>(
      `${constants.API_URL}/instructor/admin/vacations`,
    );
    setRows(data);
  }, []);
  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const add = async () => {
    if (!draft.start || !draft.end) return setError(t("vacationMissingDates"));
    if (draft.start > draft.end) return setError(t("vacationEndBeforeStart"));
    setBusy(true);
    try {
      const { data } = await axios.post<VacationRow[]>(
        `${constants.API_URL}/instructor/admin/vacations`,
        draft,
      );
      setRows(data);
      setOpen(false);
      setDraft({});
      setError("");
      alert?.showAlert(t("vacationSet"));
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(
        (typeof msg === "object" ? msg?.[lang] || msg?.he : msg) ||
          t("vacationSaveFailed"),
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    const { data } = await axios.delete<VacationRow[]>(
      `${constants.API_URL}/instructor/admin/vacations/${id}`,
    );
    setRows(data);
    alert?.showAlert(t("vacationDeleted"));
  };
  const fmt = (d: string) => moment(d).format("DD/MM/YY");

  return (
    <div className="my-3" dir={dir}>
      <Typography variant="h6">{t("vacations")}</Typography>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("noVacations")}
        </Typography>
      )}
      <div className="flex flex-col gap-1 my-2">
        {rows.map((row) => (
          <div key={row._id} className="flex items-center gap-2">
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {fmt(row.start)} – {fmt(row.end)}
            </Typography>
            <IconButton
              size="small"
              aria-label={t("deleteVacation")}
              onClick={() => remove(row._id)}
            >
              <Close fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
      <button
        className="bg-blue-200 px-4 py-1 rounded-full hover:scale-105 transition-all"
        type="button"
        onClick={() => setOpen(true)}
      >
        {t("addVacation")}
      </button>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <Box minWidth={250} p={2} dir={dir}>
          <Typography variant="h5" textAlign="center" className="pb-2">
            {t("addVacation")}
          </Typography>
          <div className="flex flex-row gap-4">
            <div>
              <div>{t("vacationStart")}</div>
              <TextField
                type="date"
                value={draft.start ?? ""}
                onChange={(e) => {
                  setError("");
                  setDraft((p) => ({ ...p, start: e.target.value }));
                }}
              />
            </div>
            <div>
              <div>{t("vacationEnd")}</div>
              <TextField
                type="date"
                value={draft.end ?? ""}
                onChange={(e) => {
                  setError("");
                  setDraft((p) => ({ ...p, end: e.target.value }));
                }}
              />
            </div>
          </div>
          <Box display="flex" justifyContent="center" minHeight={20}>
            <Typography variant="caption" color="red">
              {error}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={2} gap={2}>
            <button
              className="bg-blue-200 px-8 py-2 rounded-3xl hover:scale-105 transition-all font-medium text-blue-800 disabled:opacity-50"
              type="button"
              disabled={busy}
              onClick={add}
            >
              {t("add")}
            </button>
            <button
              className="text-gray-600 font-medium px-4 py-2 rounded-full"
              type="button"
              onClick={() => setOpen(false)}
            >
              {t("cancel")}
            </button>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
};

export default VacationsList;
